<template>
    <div
        :class="{
            'is-search-active': isCountrySearchActive,
        }"
        class="country-panel"
    >
        <div
            ref="scrollContainer"
            class="scroll"
        >
            <div class="search-sticky">
                <div class="search">
                    <Icon
                        class="search-icon"
                        icon="magnifier"
                    />

                    <input
                        ref="countrySearchInput"
                        v-model="countrySearchQuery"
                        aria-label="Search country"
                        class="search-input"
                        placeholder="Search country"
                        type="search"
                        @keydown.escape.prevent.stop="emit('escapeSearch')"
                    />
                </div>
            </div>

            <section
                aria-label="Countries"
                class="section"
                role="listbox"
            >
                <p
                    v-if="
                        isCountrySearchActive && visibleCountries.length === 0
                    "
                    class="empty"
                >
                    No countries found
                </p>

                <template
                    v-for="(section, sectionIndex) in visibleCountries"
                    :key="`country-section-${sectionIndex}`"
                >
                    <hr
                        v-if="sectionIndex > 0"
                        class="divider"
                    />

                    <div class="list">
                        <CountryOption
                            v-for="country in section.countries"
                            :key="country.isoCode"
                            :ref="
                                (el) =>
                                    setSelectedCountryRef(
                                        el,
                                        selection.isoCode === country.isoCode
                                    )
                            "
                            :country="country"
                            :selected="selection.isoCode === country.isoCode"
                            @select="emit('selectCountry', $event)"
                        />
                    </div>
                </template>
            </section>
        </div>

        <button
            :aria-expanded="isCurrencyPanelOpen"
            aria-label="Select currency"
            class="currency-footer focusable"
            type="button"
            @click.stop="emit('openCurrencyPanel')"
            @keydown.enter.prevent.stop="emit('openCurrencyPanel')"
        >
            <span class="label">Currency</span>

            <span class="control">
                <span class="value">
                    {{ currency }}
                    {{ getCurrencySymbol(currency) }}
                </span>

                <span
                    aria-hidden="true"
                    class="chevron"
                ></span>
            </span>
        </button>
    </div>
</template>

<script lang="ts" setup>
import {
    type ComponentPublicInstance,
    computed,
    nextTick,
    onMounted,
    ref,
    useTemplateRef,
} from 'vue'

import CountryOption from '@/components/app/locale/CountryOption.vue'
import Icon from '@/components/ui/icon/Icon.vue'
import { useTranslator } from '@/i18n'
import type { ShippingCountryFragment } from '@/graphql/api'
import { useCurrency } from '@/plugins/currency.ts'

const { shippingCountries, selection, isCurrencyPanelOpen } = defineProps<{
    shippingCountries: ShippingCountryFragment[]
    selection: ShippingCountryFragment['countries'][number]
    isCurrencyPanelOpen: boolean
}>()

const emit = defineEmits<{
    selectCountry: [country: ShippingCountryFragment['countries'][number]]
    openCurrencyPanel: []
    escapeSearch: []
}>()

const { getCurrencySymbol, getCountryName } = useTranslator()

const countrySearchQuery = ref('')
const countrySearchInput =
    useTemplateRef<HTMLInputElement>('countrySearchInput')
const scrollContainer = useTemplateRef<HTMLDivElement>('scrollContainer')
const selectedCountryOption = ref<InstanceType<typeof CountryOption> | null>(
    null
)

const currency = useCurrency()

const isCountrySearchActive = computed(
    (): boolean => countrySearchQuery.value.trim().length > 0
)

type CountrySections = { countries: ShippingCountryFragment['countries'] }[]

const matchesCountryWordInitials = (name: string, query: string): boolean => {
    const words = name.split(/\s+/).filter(Boolean)

    if (query.length > words.length) {
        return false
    }

    for (let index = 0; index < query.length; index += 1) {
        if (words[index]?.[0] !== query[index]) {
            return false
        }
    }

    return true
}

const visibleCountries = computed((): CountrySections => {
    const normalizedQuery = countrySearchQuery.value.trim().toLowerCase()

    if (normalizedQuery.length) {
        const filtered: ShippingCountryFragment['countries'] = []

        for (const section of shippingCountries) {
            for (const country of section.countries) {
                const name = getCountryName(country.isoCode).toLowerCase()
                const isoCode = country.isoCode.toLowerCase()

                if (
                    name.includes(normalizedQuery) ||
                    isoCode.includes(normalizedQuery) ||
                    matchesCountryWordInitials(name, normalizedQuery)
                ) {
                    filtered.push(country)
                }
            }
        }

        if (filtered.length === 0) {
            return []
        }

        return [{ countries: filtered }]
    }

    return shippingCountries
})

const focusSearchInput = () => {
    nextTick(() =>
        countrySearchInput.value?.focus({
            preventScroll: true,
        })
    )
}

const resetSearch = () => {
    countrySearchQuery.value = ''
}

const setSelectedCountryRef = (
    el: Element | ComponentPublicInstance | null,
    isSelected: boolean
) => {
    if (!isSelected) {
        return
    }

    selectedCountryOption.value = el as InstanceType<
        typeof CountryOption
    > | null
}

const getStickyOffset = (container: HTMLElement): number => {
    const value = getComputedStyle(container)
        .getPropertyValue('--country-panel-sticky-offset')
        .trim()
    const parsed = Number.parseInt(value, 10)

    return Number.isNaN(parsed) ? 64 : parsed
}

const scrollToSelectedCountry = () => {
    nextTick(() => {
        const container = scrollContainer.value
        const optionElement = selectedCountryOption.value?.rootElement

        if (!container || !optionElement) {
            return
        }

        const stickyOffset = getStickyOffset(container)
        const scrollTop =
            container.scrollTop +
            optionElement.getBoundingClientRect().top -
            container.getBoundingClientRect().top -
            stickyOffset

        container.scrollTop = Math.max(0, scrollTop)
    })
}

onMounted(() => {
    scrollToSelectedCountry()
})

defineExpose({
    focusSearchInput,
    resetSearch,
})
</script>

<style lang="scss" scoped>
.country-panel {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;

    &:not(.is-search-active) .scroll {
        max-height: 350px;

        @include min-width(1024) {
            max-height: 370px;
        }
    }

    .scroll {
        --country-panel-sticky-offset: 64px;
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        padding: 0 calc(var(--spacing-16, 16px) + 4px) var(--spacing-16, 16px);
        -webkit-overflow-scrolling: touch;

        @include min-width(640) {
            padding: 0 var(--spacing-16, 16px) var(--spacing-16, 16px);
        }

        @include min-width(1024) {
            --country-panel-sticky-offset: 68px;
            padding: 0 var(--spacing-20i, 20px) var(--spacing-20i, 20px);
        }
    }

    .search-sticky {
        position: sticky;
        top: 0;
        z-index: 1;
        margin: 0 calc(-1 * (var(--spacing-16, 16px) + 4px));
        padding: var(--spacing-12, 12px) calc(var(--spacing-16, 16px) + 4px)
            var(--spacing-12, 12px);
        background: $white;

        @include min-width(640) {
            margin: 0 calc(-1 * var(--spacing-16, 16px));
            padding: var(--spacing-12, 12px) var(--spacing-16, 16px)
                var(--spacing-12, 12px);
        }

        @include min-width(1024) {
            margin: 0 calc(-1 * var(--spacing-20i, 20px));
            padding: var(--spacing-14, 14px) var(--spacing-20i, 20px)
                var(--spacing-12, 12px);
        }
    }

    .search {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        border: 1px solid $grey-200;
        border-radius: var(--border-radius-m, 8px);
        background: $white;

        .search-icon {
            flex-shrink: 0;
            font-size: 14px;
            color: $grey-500;
        }

        .search-input {
            width: 100%;
            border: none;
            background: transparent;
            font-family: inherit;
            font-size: 1rem;
            line-height: 1.3;
            color: $slate;

            @include min-width(1024) {
                font-size: 0.875rem;
            }

            &::placeholder {
                color: $grey-500;
            }

            &:focus {
                outline: none;
            }
        }
    }

    .section {
        margin: 0;
    }

    .empty {
        margin: 0;
        padding: 12px 4px;
        font-size: 0.875rem;
        color: $grey-500;
        text-align: center;
    }

    .divider {
        border: none;
        border-top: 1px solid $grey-200;
        margin: 14px 0;
    }

    .list {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .currency-footer {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        width: 100%;
        padding: 12px calc(var(--spacing-16, 16px) + 12px);
        border: none;
        border-top: 1px solid $grey-200;
        border-radius: 0;
        background: $white;
        font-family: inherit;
        text-align: left;
        cursor: pointer;
        user-select: none;

        @include min-width(1024) {
            padding: 14px calc(var(--spacing-20i, 20px) + 14px);
        }

        &.focusable {
            &:focus {
                outline: none;
            }

            &:focus-visible {
                outline: 2px solid $blue;
                outline-offset: -2px;
            }
        }

        .label {
            font-size: 0.875rem;
            font-weight: 400;
            color: $slate;
            letter-spacing: normal;
            text-transform: none;

            @include min-width(1024) {
                font-size: 0.9375rem;
            }
        }

        .control {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            min-height: 36px;
            padding: 0 12px;
            border: 1px solid $grey-200;
            border-radius: var(--border-radius-m, 8px);
            background: $white;
            font-size: 0.8125rem;
            font-weight: 400;
            color: $slate;
            letter-spacing: 0.02em;
            white-space: nowrap;

            .value {
                line-height: 1;
            }

            .chevron {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                font-family: $icon-font;
                font-weight: 600;
                font-size: 10px;
                line-height: 1;
                color: $grey-500;

                &::before {
                    content: $icon-angle-down;
                }
            }
        }
    }
}
</style>
